
import Auth from "@/lib/common/auth";
import { Component, Vue } from "vue-property-decorator";
import MsgRequest, {
  DelExportExcelParam,
  ExportListParams
} from "@/lib/model/message/MsgRequest";
// 引入新的v2 API
import {
  getExportTaskList,
  deleteExportTask,
  pauseExportTask,
  restartExportTask,
  getExportResult,
  trackDownload,
  getCandidateListFeatures,
  ExportTaskStatus,
  ExportTaskType,
  ExportVersion,
  ExportTaskListParams,
  ExportTaskItem
} from "@/lib/api/v2/export-task";
import _ from "lodash-es";
import Loading from "../../components/loading.vue";
import DelExamModal from "../paper/components/del-new-modal.vue";
import ModalTitle from "@/lib/model/exam/modal-title";
import dayjs from "dayjs";

class ExportStatus {
  label = "";
  value = 0;
}

class PageSizeOptions {
  value = 10;
}

// 筛选表单接口 - 匹配新的API参数结构
interface SearchForm {
  exportId: string; // 导出任务ID筛选
  name: string; // 任务名称筛选
  taskTypeList: string[]; // 任务类型列表筛选
  taskStatusList: number[]; // 任务状态列表筛选
  versionList: number[]; // 版本列表筛选
  userId: string; // 创建人ID筛选
  createdTimeRange: string[]; // 创建时间范围筛选
}

@Component({
  components: {
    Loading,
    DelExamModal
  }
})
export default class ExportList extends Vue {
  name = "export-list";
  searchTitle = "";
  searchText = "";
  hasNoList = false;
  dataList: ExportTaskItem[] = [];
  tableLoading = false;
  page = 1;
  currentPageSize = 10;
  total = 0;
  exportListParams = new ExportListParams();
  delExportExcelParam = new DelExportExcelParam();
  loadingList = true;
  showDelConfirm = false;
  isShowDelModal = false;
  modalTitle: ModalTitle = new ModalTitle();

  // 新增：密码查看弹窗
  showPasswordDialog = false;
  currentPassword = "";

  // 新增：筛选表单数据 - 匹配新的API参数结构
  searchForm: SearchForm = {
    exportId: "",
    name: "",
    taskTypeList: [],
    taskStatusList: [],
    versionList: [],
    userId: "",
    createdTimeRange: []
  };

  pageSizeOptions: PageSizeOptions[] = [
    { value: 10 },
    { value: 20 },
    { value: 50 },
    { value: 100 }
  ];

  // 更新：任务类型选项（基于新的v2 API）
  taskTypeOptions = [
    // { label: "全部", value: "" },
    { label: "候选人综合数据", value: ExportTaskType.CAND },
    { label: "候选人答题明细", value: ExportTaskType.CAND_ANS }
    // { label: "面试配置", value: ExportTaskType.EXAM },
    // { label: "面试题目", value: ExportTaskType.EXAM_QUES }
  ];

  // 版本选项
  versionOptions = [
    { label: "全部", value: "" },
    { label: "旧版本数据", value: ExportVersion.V0 },
    { label: "新版本数据", value: ExportVersion.V1 }
  ];

  // 更新：任务状态选项（基于新的枚举）
  statusOptions = [
    { label: "全部", value: "" },
    { label: "等待执行", value: ExportTaskStatus.WAITING },
    { label: "执行中", value: ExportTaskStatus.RUNNING },
    { label: "执行成功", value: ExportTaskStatus.SUCCESS },
    { label: "执行失败", value: ExportTaskStatus.FAILED },
    { label: "已暂停", value: ExportTaskStatus.PAUSED },
    { label: "已过期", value: ExportTaskStatus.EXPIRED }
  ];

  // 新增：用户选项（从 /aiExam/candidate/listFeatures 接口获取）
  userOptions: { label: string; value: number }[] = [];
  // 新增：完整的用户选项列表（用于搜索时过滤）
  allUserOptions: { label: string; value: number }[] = [];

  rowStyle = {
    color: "#8B8F94",
    "font-weight": "400",
    "font-size": "14px"
  };

  mounted() {
    this.getDataList();
    this.getUserOptions(); // 获取用户选项
    this.modalTitle.title = "删除任务";
    this.modalTitle.hasDesc = false;
    this.modalTitle.desc = "删除任务";
  }

  getDataList(str = "") {
    if (str === "pageSize") {
      this.page = 1;
    }
    this.tableLoading = true;

    // 构建新的v2 API参数 - 匹配Go接口文档
    const params: ExportTaskListParams = {
      page: this.page,
      pageSize: this.currentPageSize,
      features: {}
    };

    // 处理筛选条件
    if (this.searchForm.exportId) {
      params.features!.exportId = parseInt(this.searchForm.exportId);
    }

    if (this.searchForm.name) {
      params.features!.name = this.searchForm.name;
    }

    if (
      this.searchForm.taskStatusList &&
      this.searchForm.taskStatusList.length > 0
    ) {
      // 过滤掉空值
      const filteredStatus = this.searchForm.taskStatusList.filter(
        status => status !== ""
      );
      if (filteredStatus.length > 0) {
        params.features!.taskStatusList = filteredStatus as ExportTaskStatus[];
      }
    }

    if (
      this.searchForm.taskTypeList &&
      this.searchForm.taskTypeList.length > 0
    ) {
      // 过滤掉空值
      const filteredTypes = this.searchForm.taskTypeList.filter(
        type => type !== ""
      );
      if (filteredTypes.length > 0) {
        params.features!.taskTypeList = filteredTypes as ExportTaskType[];
      }
    }

    if (this.searchForm.versionList && this.searchForm.versionList.length > 0) {
      // 过滤掉空值
      const filteredVersions = this.searchForm.versionList.filter(
        version => version !== ""
      );
      if (filteredVersions.length > 0) {
        params.features!.versionList = filteredVersions as ExportVersion[];
      }
    }

    if (this.searchForm.userId) {
      params.features!.userId = parseInt(this.searchForm.userId);
    }

    // 处理时间范围 - 使用字符串格式而不是时间戳
    if (
      this.searchForm.createdTimeRange &&
      this.searchForm.createdTimeRange.length === 2
    ) {
      const [start, end] = this.searchForm.createdTimeRange;
      if (start && end) {
        params.features!.createdTime = [start, end];
      }
    }

    // 使用新的v2 API
    getExportTaskList(params)
      .then(res => {
        if (res.code === 0) {
          this.total = res.data.count;
          this.dataList = res.data.list;
        } else {
          this.$message.error(res.message);
        }
      })
      .catch(error => {
        console.error("获取导出任务列表失败:", error);
        this.$message.error("获取导出任务列表失败");
      })
      .finally(() => {
        this.loadingList = false;
        this.tableLoading = false;
      });
  }

  async downloadExcel(row: ExportTaskItem) {
    try {
      // 统一使用getResult API获取下载链接（根据用户说明，旧数据也用同样的下载逻辑）
      const result = await getExportResult({ exportId: row.exportId });
      if (result.code === 0 && result.data.url) {
        // 记录下载操作
        await trackDownload({
          url: result.data.url,
          taskId: row.exportId,
          filename: row.name
        }).catch(error => {
          console.error("下载跟踪记录失败:", error);
        });

        // 执行下载
        const a = document.createElement("a");
        a.href = result.data.url;
        a.target = "_blank";
        a.setAttribute("download", row.name);
        a.click();
        a.remove();
      } else {
        this.$message.error("获取下载链接失败");
      }
    } catch (error) {
      console.error("下载失败:", error);
      this.$message.error("下载失败，请稍后重试");
    }
  }

  deleteExcelParam(row: ExportTaskItem) {
    // 根据新需求：等待中/执行成功/执行失败/已暂停/已过期 可以删除
    // 执行中的任务不允许删除
    if (this.getTaskStatus(row) === ExportTaskStatus.RUNNING) {
      this.$message.warning("正在执行中的任务无法删除，请稍后再试");
      return;
    }

    this.delExportExcelParam.exportId = row.exportId;
    this.isShowDelModal = true;
  }

  delCancel() {
    this.isShowDelModal = false;
  }

  async deleteExcel() {
    try {
      const result = await deleteExportTask({
        exportId: this.delExportExcelParam.exportId
      });
      if (result.code === 0) {
        this.$message.success("删除成功");
      } else {
        this.$message.error(result.message);
      }
    } catch (error) {
      console.error("删除失败:", error);
      this.$message.error("删除失败，请稍后重试");
    } finally {
      this.isShowDelModal = false;
      this.getDataList();
    }
  }

  downFilesCount(row: ExportTaskItem) {
    return row?.urlList?.length ?? 1;
  }

  // 新增：获取任务状态（兼容新旧字段）
  getTaskStatus(row: ExportTaskItem): number {
    return row.taskStatus ?? row.status ?? 0;
  }

  // 新增：获取任务类型（兼容新旧字段）
  getTaskType(row: ExportTaskItem): string {
    return row.taskType ?? row.exportType ?? "";
  }

  // 新增：获取开始时间（兼容新旧字段）
  getStartTime(row: ExportTaskItem): string {
    return row.startTime ?? row.beginTime ?? "";
  }

  // 新增：获取用户信息（兼容新旧字段）
  getUserInfo(row: ExportTaskItem): any {
    return row.userCompany ?? row.userInfo ?? {};
  }

  // 新增：获取用户选项
  async getUserOptions() {
    try {
      const params = {
        companyId: Auth.companyId,
        examType: ""
      };
      const result = await getCandidateListFeatures(params);
      if (result.code === 0) {
        // 将用户数据转换为选项格式
        const users = result.data.user.map(user => ({
          label: user.nickname,
          value: user.userId
        }));
        this.allUserOptions = users; // 保存完整列表
        this.userOptions = users; // 显示列表
      } else {
        console.warn("获取用户列表失败:", result.message);
      }
    } catch (error) {
      console.error("获取用户选项失败:", error);
    }
  }

  // 新增：搜索用户（远程搜索）
  searchUsers(query: string) {
    if (query) {
      // 根据输入过滤用户选项
      this.userOptions = this.allUserOptions.filter(item => {
        return item.label.toLowerCase().includes(query.toLowerCase());
      });
    } else {
      // 如果查询为空，显示完整的用户列表
      this.userOptions = this.allUserOptions;
    }
  }

  // 新增：重置搜索表单
  resetSearch() {
    this.searchForm = {
      exportId: "",
      name: "",
      taskTypeList: [],
      taskStatusList: [],
      versionList: [],
      userId: "",
      createdTimeRange: []
    };
    this.page = 1;
    // 重置时恢复完整的用户选项
    this.userOptions = this.allUserOptions;
    this.getDataList();
  }

  // 新增：获取导出类型标签
  getExportTypeLabel(taskType: string): string {
    const option = this.taskTypeOptions.find(item => item.value === taskType);
    return option ? option.label : "";
  }

  // 新增：格式化数据条数（基于版本差异化）
  formatCount(row: ExportTaskItem): string {
    // 旧数据显示"-"
    if (row.version === 0) {
      return "-";
    }
    // 只有任务执行成功才显示数据条数
    if (this.getTaskStatus(row) !== ExportTaskStatus.SUCCESS) {
      return "-";
    }
    // 导出报告类型展示报告数量
    if (this.getTaskType(row) === "candidatePdfExport") {
      return row.count === undefined ? "-" : `${row.count}份报告`;
    }
    // 其他类型展示导出数据条目
    return row.count === undefined ? "-" : `${row.count}条`;
  }

  // 新增：格式化文件数量（基于版本差异化）
  formatFileCount(row: ExportTaskItem): string {
    // 旧数据使用原有逻辑
    if (row.version === 0) {
      return this.downFilesCount(row).toString() + "个";
    }
    // 只有任务执行成功才显示文件数量
    if (this.getTaskStatus(row) !== ExportTaskStatus.SUCCESS) {
      return "-";
    }
    return row.fileCount === undefined ? "-" : `${row.fileCount}个`;
  }

  // 新增：格式化任务耗时
  formatDuration(row: ExportTaskItem): string {
    // 旧数据显示"-"
    if (row.version === 0) {
      return "-";
    }
    // 任务未执行完成显示'-'
    const status = this.getTaskStatus(row);
    if (
      status !== ExportTaskStatus.SUCCESS &&
      status !== ExportTaskStatus.FAILED &&
      status !== ExportTaskStatus.PAUSED &&
      status !== ExportTaskStatus.EXPIRED
    ) {
      return "-";
    }

    const startTime = this.getStartTime(row);
    const endTime = row.endTime;
    if (!startTime || !endTime) return "-";

    const begin = dayjs(startTime);
    const end = dayjs(endTime);
    const duration = end.diff(begin, "second");

    if (duration <= 0) return "1s";

    // 大于24小时显示天小时分钟
    if (duration >= 86_400) {
      const days = Math.floor(duration / 86_400);
      const hours = Math.floor((duration % 86_400) / 3600);
      const minutes = Math.floor((duration % 3600) / 60);
      return `${days}天${hours}h${minutes}min`;
    }

    // 大于60分钟显示小时分钟
    if (duration >= 3600) {
      const hours = Math.floor(duration / 3600);
      const minutes = Math.floor((duration % 3600) / 60);
      return `${hours}h${minutes}min`;
    }

    // 大于60秒显示分钟秒
    if (duration >= 60) {
      const minutes = Math.floor(duration / 60);
      const seconds = duration % 60;
      return `${minutes}min${seconds}s`;
    }

    return `${duration}s`;
  }

  // 新增：格式化时间
  formatTime(time?: string): string {
    if (!time || time === "0001-01-01 00:00:00") return "-";
    return dayjs(time).format("YYYY-MM-DD HH:mm:ss");
  }

  // 新增：格式化创建人（基于版本差异化）
  formatCreator(row: ExportTaskItem): string {
    // 旧数据显示"-"
    if (row.version === 0) {
      return "-";
    }
    const userInfo = this.getUserInfo(row);
    return userInfo?.nickname || "-";
  }

  // 新增：查看密码
  showPassword(row: ExportTaskItem) {
    this.currentPassword = row.passwd || "";
    this.showPasswordDialog = true;
  }

  // 新增：暂停任务 - 等待中 -> 已暂停
  async pauseTask(row: ExportTaskItem) {
    try {
      const result = await pauseExportTask({ exportId: row.exportId });
      if (result.code === 0) {
        this.$message.success("暂停成功");
        this.getDataList();
      } else {
        this.$message.error(result.message);
      }
    } catch (error) {
      console.error("暂停任务失败:", error);
      this.$message.error("暂停任务失败，请稍后重试");
    }
  }

  // 新增：重新排队任务 - 执行失败/暂停 -> 排队中
  async restartTask(row: ExportTaskItem) {
    try {
      const result = await restartExportTask({ exportId: row.exportId });
      if (result.code === 0) {
        this.$message.success("重新排队成功");
        this.getDataList();
      } else {
        this.$message.error(result.message);
      }
    } catch (error) {
      console.error("重新排队失败:", error);
      this.$message.error("重新排队失败，请稍后重试");
    }
  }

  formatSendStatus(val: number) {
    switch (val) {
      case ExportTaskStatus.WAITING:
        return "等待中";
      case ExportTaskStatus.RUNNING:
        return "执行中";
      case ExportTaskStatus.SUCCESS:
        return "执行成功";
      case ExportTaskStatus.FAILED:
        return "执行失败";
      case ExportTaskStatus.PAUSED:
        return "已暂停";
      case ExportTaskStatus.DELETED:
        return "已删除";
      case ExportTaskStatus.EXPIRED:
        return "已过期";
      default:
        return `未知状态(${val})`;
    }
  }

  formatColorStatus(val: number) {
    switch (val) {
      case ExportTaskStatus.WAITING:
      case ExportTaskStatus.RUNNING:
        return "download-color-2"; // 橙色 - 进行中
      case ExportTaskStatus.SUCCESS:
        return "download-color-3"; // 绿色 - 成功
      case ExportTaskStatus.FAILED:
        return "download-color-4"; // 红色 - 失败
      case ExportTaskStatus.PAUSED:
        return "download-color-4"; // 红色 - 已暂停
      case ExportTaskStatus.DELETED:
        return "download-color-1"; // 灰色 - 已删除
      case ExportTaskStatus.EXPIRED:
        return "download-color-1"; // 灰色 - 已过期
      default:
        return "download-color-2"; // 默认橙色
    }
  }

  columnStyle(row: any) {
    if (row.columnIndex === 0) {
      return "border-right: 1px solid #eaedf7 !important;";
    }
  }

  cellStyle(cell: any) {
    if (cell.rowIndex === 0 && cell.columnIndex === 0) {
      return "border-right: 1px solid #eaedf7 !important;";
    }
  }
}
